import { useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { Command } from 'cmdk'
import { ChevronDown, UserPlus } from 'lucide-react'
import { useAnalysts } from '../../hooks/useAnalysts'
import { useMe } from '@/features/auth/hooks'
import type { AnalystSummary } from '../../types'

interface Props {
  /** Renders the picker as a button-like trigger with this label. */
  triggerLabel?: string
  /** Inverted color trigger for the BulkActionsBar (which is on bg-fg). */
  inverted?: boolean
  onPick: (analyst: AnalystSummary | { user_id: 'me' }) => void
}

/**
 * AF-126 — analyst picker used by both the bulk-actions bar and the action
 * rail. "Assign to me" is always at the top so the common case is one click;
 * other analysts are searchable via cmdk.
 */
export function AnalystPicker({ triggerLabel = 'Assign…', inverted, onPick }: Props) {
  const [open, setOpen] = useState(false)
  const { data: analysts = [] } = useAnalysts()
  const { data: me } = useMe()

  const others = analysts.filter((a) => a.user_id !== me?.id)

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        className={
          inverted
            ? 'flex items-center gap-1 rounded-full px-3 py-1 text-sm text-bg hover:bg-bg/10'
            : 'flex h-9 items-center gap-2 rounded border border-border px-3 text-sm hover:bg-muted'
        }
      >
        <UserPlus size={12} />
        <span>{triggerLabel}</span>
        <ChevronDown size={10} className={inverted ? 'opacity-70' : 'text-muted-fg'} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-72 rounded-md border border-border bg-popover p-1"
        >
          <Command label="Pick an analyst">
            <Command.Input
              placeholder="Search analysts…"
              className="h-8 w-full bg-transparent px-2 text-sm outline-none placeholder:text-muted-fg"
            />
            <Command.List className="max-h-64 overflow-y-auto">
              <Command.Empty className="px-2 py-2 text-xs text-muted-fg">
                No analysts match.
              </Command.Empty>
              {me && (
                <Command.Group heading="">
                  <Command.Item
                    value="assign-to-me"
                    onSelect={() => {
                      setOpen(false)
                      onPick({ user_id: 'me' })
                    }}
                    className="flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1.5 text-sm aria-selected:bg-muted data-[selected=true]:bg-muted"
                  >
                    <span className="flex items-center gap-2">
                      <Avatar initials="YOU" />
                      <span>Assign to me</span>
                    </span>
                  </Command.Item>
                </Command.Group>
              )}
              {others.length > 0 && (
                <div className="px-2 pb-1 pt-2 text-[0.625rem] uppercase tracking-wide text-muted-fg">
                  Team
                </div>
              )}
              {others.map((a) => (
                <Command.Item
                  key={a.user_id}
                  value={`${a.display_name} ${a.initials}`}
                  onSelect={() => {
                    setOpen(false)
                    onPick(a)
                  }}
                  className="flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1.5 text-sm aria-selected:bg-muted data-[selected=true]:bg-muted"
                >
                  <span className="flex items-center gap-2">
                    <Avatar initials={a.initials} />
                    <span>{a.display_name}</span>
                  </span>
                  <span className="font-mono text-[0.625rem] text-muted-fg">
                    {a.open_count} open
                  </span>
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

function Avatar({ initials }: { initials: string }) {
  return (
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[0.5rem] font-semibold uppercase text-muted-fg">
      {initials.slice(0, 2)}
    </span>
  )
}
